/**
 * WHICH RECORDINGS BELONG TO THE SAME CONVERSATION, and what the lanes of one
 * file are called.
 *
 * Three rules, extracted from `audio-player.tsx` so they can be exercised without
 * rendering a canvas — see `tests/legcheck.mjs`. They were inline until a
 * transferred call proved all three of them wrong at once, and a rule that only
 * exists inside a `useEffect` is a rule nothing can check.
 *
 * ---------------------------------------------------------------------------
 * THE ASSUMPTION THE PLAYER IS BUILT ON: every lane it draws is the same
 * conversation, simultaneous, starting at the same instant. One clock, one
 * playhead, every buffer started at the same offset. True of the two sides of a
 * browser call, and true of the two channels of a carrier file.
 *
 * FALSE FOR A TRANSFERRED CALL, which owns TWO carrier recordings:
 * `telephony-dual` (caller ↔ AI) and `telephony-transfer` (caller ↔ human). The
 * second BEGINS AFTER THE FIRST ONE ENDED. On the first live transfer, handing
 * both to one player produced three defects in one picture:
 *
 *   1. Both conversations played at once, over each other.
 *   2. The second was drawn from x=0 on the first's axis — a picture claiming a
 *      human was speaking while the AI still had the call.
 *   3. Only the transfer file's channel 0 was drawn at all, because the
 *      dual-channel split was gated on the literal track name `telephony-dual`.
 *      The HUMAN's side of that conversation was missing from the screen with
 *      nothing saying so.
 *
 * None of the three raised an error, and two of them looked like features.
 *
 * A TAKEN-OVER CALL HAS THE SAME SHAPE. When a person takes a call over from the
 * panel, everything after that moment is one more recording, `takeover`: the
 * caller on the left channel and the person on the right, recorded by our own
 * Durable Object as it relayed them (docs/hitl.md §5). Like the transfer file it
 * starts where the AI's conversation stopped, has its own clock, and has no
 * transcript, turns or cost behind it. So it is its own leg and its own player,
 * by the same rules.
 */

/** The minimum a recording needs for these rules to apply to it. */
export interface LegRecording {
  track: string
  state: string
  s3Key: string | null
}

/**
 * The track a transferred call's SECOND conversation lives in.
 *
 * A constant rather than three string literals, because the splitter, the
 * backstop and the lane labels all have to agree about it and a typo in any one
 * of them is a silently wrong picture rather than an error.
 */
export const TRANSFER_TRACK = 'telephony-transfer'

/** The track a taken-over call's second conversation lives in. See the header. */
export const TAKEOVER_TRACK = 'takeover'

/** Which conversation a track belongs to. */
function legOf(track: string): 'conversation' | 'transfer' | 'takeover' {
  if (track === TRANSFER_TRACK) return 'transfer'
  if (track === TAKEOVER_TRACK) return 'takeover'
  return 'conversation'
}

/**
 * SPLIT A SESSION'S RECORDINGS INTO CONVERSATIONS.
 *
 * `conversation` is what the session's transcript, turns and cost describe.
 * `transfer` and `takeover` are audio and a duration and nothing else — the
 * sideband ended with the AI's leg, so there is no transcript, no turn timeline
 * and no token cost behind either.
 *
 * They share a session id and nothing else: not a clock, not a start time, not
 * a transcript. A page renders one player per non-empty group.
 */
export function splitRecordingsByLeg<T extends LegRecording>(
  recordings: readonly T[],
): { conversation: T[]; transfer: T[]; takeover: T[] } {
  return {
    conversation: recordings.filter((r) => legOf(r.track) === 'conversation'),
    transfer: recordings.filter((r) => legOf(r.track) === 'transfer'),
    takeover: recordings.filter((r) => legOf(r.track) === 'takeover'),
  }
}

/**
 * Playable rows, in display order: the assistant speaks first on most calls, so
 * its lane goes on top; the carrier file replaces both on telephony.
 *
 * ⚠️ ONLY ONE CONVERSATION IS EVER PLAYED, and that is a backstop rather than
 * the mechanism — `splitRecordingsByLeg` is. Handed rows from more than one, it
 * keeps the conversation the transcript describes when that is present, and
 * otherwise the transfer before the takeover. It exists because the failure it
 * prevents is SILENT: a caller that hands over the whole array gets two
 * conversations playing at once and one of them drawn in the wrong place, with
 * nothing on screen saying so. Degrading to "play the conversation the
 * transcript describes" is wrong in a way somebody notices; the overlay is wrong
 * in a way that looks like a feature.
 *
 * When a second conversation is the ONLY thing present it plays normally, which
 * is what makes a page's second player work.
 */
export function playableTracks<T extends LegRecording>(recordings: readonly T[]): T[] {
  const order: Record<string, number> = { assistant: 0, user: 1, 'telephony-dual': 2 }
  const ready = recordings.filter((r) => r.state === 'READY' && r.s3Key != null)
  const legs = new Set(ready.map((r) => legOf(r.track)))
  const keep = legs.has('conversation') ? 'conversation' : legs.has('transfer') ? 'transfer' : 'takeover'
  return ready
    .filter((r) => legOf(r.track) === keep)
    .sort((a, b) => (order[a.track] ?? 9) - (order[b.track] ?? 9))
}

/**
 * What to call the lanes of one decoded file.
 *
 * A CARRIER FILE IS `record-from-answer-dual`: channel 1 is the caller and
 * channel 2 is whoever they were bridged to. BOTH carrier tracks have that shape,
 * which is exactly what the old `track === 'telephony-dual'` gate got wrong — a
 * transfer recording rendered channel 0 only, and the human's side of the
 * conversation was absent from the screen.
 *
 * So the split is decided by the CHANNEL COUNT plus "is this a two-party file",
 * and only the second label differs: who the caller was talking to is the whole
 * difference between them. The takeover recording is two-party too — caller
 * left, the person right — though our server made it, not a carrier.
 *
 * Anything else — the browser's own `user` and `assistant` captures — is one mono
 * file per lane and keeps its track name.
 */
export function lanesFor(track: string, channels: number): string[] {
  if (channels >= 2 && (track.startsWith('telephony-') || track === TAKEOVER_TRACK)) {
    return ['caller', track === TRANSFER_TRACK || track === TAKEOVER_TRACK ? 'human' : 'assistant']
  }
  return [track]
}

/** True for a file whose two channels are the two sides of one conversation,
 *  so a stereo export passes it through rather than mixing it down. */
export function isTwoPartyFile(track: string, channels: number): boolean {
  return lanesFor(track, channels).length === 2
}
